import { useQuery } from '@tanstack/preact-query';
import { useContext, useMemo } from 'preact/hooks';
import { ApiClientContext } from '../arcticapi';
import type { LineAsset, LineAssets } from '../arcticapi/types';
import type { TimetableDetailsLine } from './timetableDetailsLine';

export interface UseLineAssetsState {
  loading: boolean;
  error: Error | null;
  assets: LineAssets | null;
}

const LINE_ASSET_CATEGORIES: Array<keyof LineAssets> = [
  'timetable',
  'map',
  'fares',
];

/**
 * Fetch and filter the line assets for the selected line and date.
 */
export function useLineAssets(
  selectedLine: TimetableDetailsLine | null,
  selectedDate: string,
): UseLineAssetsState {
  const client = useContext(ApiClientContext);
  const assetsHref = selectedLine?.assetsHref ?? null;

  const assetsQuery = useQuery<LineAssets, Error>({
    queryKey: ['line-assets', selectedLine?.id, assetsHref],
    enabled: Boolean(client) && Boolean(assetsHref),
    queryFn: ({ signal }) => {
      if (!client || !assetsHref) {
        throw new Error('Line assets request is missing an assets link.');
      }

      return client.getLineAssets(assetsHref, { signal });
    },
  });

  // Cache the filtered assets for the date, so that we don't have to re-filter on every render
  const assets = useMemo(
    () =>
      assetsQuery.data
        ? filterLineAssetsForDate(assetsQuery.data, selectedDate)
        : null,
    [assetsQuery.data, selectedDate],
  );

  return {
    loading: Boolean(assetsHref) && assetsQuery.isPending,
    error: assetsQuery.error,
    assets,
  };
}

/**
 * Filters the line assets to include only those that are effective for the timetable date.
 */
export function filterLineAssetsForDate(
  assets: LineAssets,
  selectedDate: string,
): LineAssets {
  const selectedDateTimestamp = parseAssetDate(selectedDate);

  if (selectedDateTimestamp === null) {
    return assets;
  }

  return LINE_ASSET_CATEGORIES.reduce<LineAssets>(
    (filteredAssets, category) => ({
      ...filteredAssets,
      [category]: getLatestApplicableAssets(
        assets[category],
        selectedDateTimestamp,
      ),
    }),
    {
      timetable: [],
      map: [],
      fares: [],
    },
  );
}

/**
 * Gets the latest applicable assets for the given date
 *
 * The asset has to already be effective on the selected date,
 * so we exclude any assets with an effectiveFrom date in the future.
 *
 * If there are multiple then we get the most recent one.
 */
function getLatestApplicableAssets(
  assets: LineAsset[],
  selectedDateTimestamp: number,
): LineAsset[] {
  let latestTimestamp: number | null = null;

  for (const asset of assets) {
    const effectiveFromTimestamp = parseAssetDate(asset.effectiveFrom);

    if (
      effectiveFromTimestamp === null ||
      effectiveFromTimestamp > selectedDateTimestamp
    ) {
      // This asset isn't effective yet, so skip it
      continue;
    }

    if (latestTimestamp === null || effectiveFromTimestamp > latestTimestamp) {
      // This asset is the most recent effective one we've seen so far, so update the latest timestamp
      latestTimestamp = effectiveFromTimestamp;
    }
  }

  if (latestTimestamp === null) {
    return [];
  }

  return assets.filter(
    (asset) => parseAssetDate(asset.effectiveFrom) === latestTimestamp,
  );
}

function parseAssetDate(value: string): number | null {
  const dateOnlyMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);

  if (dateOnlyMatch) {
    const [, year, month, day] = dateOnlyMatch;
    return Date.UTC(Number(year), Number(month) - 1, Number(day));
  }

  const timestamp = Date.parse(value);
  return Number.isNaN(timestamp) ? null : timestamp;
}
